import React, {useState, useEffect} from "react"
import {formatTextTestIds} from "@next/utils"
import {Collapse} from "@mui/material"
import {TextModel} from "models/Text"
import connect from "./connect"
import Facet from "../facet"
import {StyledFacetsContainer} from "./components"
import {ViewLinkOptions} from "../../config/constants"
import FacetsButton from "../facetsButton"
import {FacetsState} from "../../models/FacetsState"

interface FacetsProps {
    filterName: string
    facetName: string
    facets: FacetsState
    toggleViewMore: ({facetName, filterName}: {facetName: string; filterName: string}) => void
    openViewAllModal: ({facetName, filterName}: {facetName: string; filterName: string}) => void
    handleSetFacet: (facetValue: string) => void
    isFetchingPageItems: boolean
    text: TextModel
    isViewMoreOpen?: boolean
    viewLink?: ViewLinkOptions
    firstFacets: string[]
    lastFacets?: string[]
    firstItemRef?: React.RefObject<HTMLInputElement>
}

export const Facets = ({
    filterName,
    facetName,
    isViewMoreOpen,
    toggleViewMore,
    openViewAllModal,
    viewLink,
    firstFacets,
    lastFacets,
    facets,
    handleSetFacet,
    isFetchingPageItems,
    text,
    firstItemRef,
}: FacetsProps) => {
    const [first, setFirstFacets] = useState(firstFacets)
    const [last, setLastFacets] = useState(lastFacets)
    const [facetValueTooltipOpen, setFacetValueTooltipOpen] = useState<string>("")

    const facetTooltipHandler = (facetValue: string) => {
        setFacetValueTooltipOpen(facetValue)
    }

    useEffect(() => {
        if (isFetchingPageItems) {
            setFirstFacets(first)
            setLastFacets(last)
        } else {
            setFirstFacets(firstFacets)
            setLastFacets(lastFacets)
        }
    }, [isFetchingPageItems]) // eslint-disable-line react-hooks/exhaustive-deps

    const [firstPrefoldFacet, ...otherPrefoldFacets] = first

    return (
        <StyledFacetsContainer>
            <Facet
                facet={facets[firstPrefoldFacet]}
                handleSetFacet={handleSetFacet}
                facetTooltipHandler={facetTooltipHandler}
                facetValueTooltipOpen={facetValueTooltipOpen}
                innerRef={firstItemRef}
            />
            {otherPrefoldFacets.map(option => (
                <Facet
                    key={`${option}-facet`}
                    facet={facets[option]}
                    handleSetFacet={handleSetFacet}
                    facetTooltipHandler={facetTooltipHandler}
                    facetValueTooltipOpen={facetValueTooltipOpen}
                />
            ))}
            {viewLink === ViewLinkOptions.viewMore && last && (
                <Collapse in={isViewMoreOpen}>
                    <StyledFacetsContainer>
                        {last.map(option => (
                            <Facet
                                key={`${option}-facet`}
                                facet={facets[option]}
                                handleSetFacet={handleSetFacet}
                                facetTooltipHandler={facetTooltipHandler}
                                facetValueTooltipOpen={facetValueTooltipOpen}
                            />
                        ))}
                    </StyledFacetsContainer>
                </Collapse>
            )}
            {viewLink === ViewLinkOptions.viewMore && (
                <FacetsButton
                    text={isViewMoreOpen ? text.buttons.viewLess : text.buttons.viewMore}
                    data-testid={formatTextTestIds("plp-view-more-button")}
                    onClick={() => toggleViewMore({facetName, filterName})}
                />
            )}
            {viewLink === ViewLinkOptions.viewAll && (
                <FacetsButton
                    text={text.buttons.viewAll}
                    data-testid={formatTextTestIds("plp-view-all-button")}
                    aria-haspopup="dialog"
                    onClick={() => openViewAllModal({facetName, filterName})}
                />
            )}
        </StyledFacetsContainer>
    )
}

export default connect(Facets)
